import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "../../../routes";
import { SearchBar } from "@legal-500/legal500-design-system";
import {
  CmsEmptyState,
  CmsFilterRow,
  CmsGroupedTable,
  CmsStackedBar,
  StatusBadge,
  getStatusLabel,
} from "../../../components/ResearchCms";
import { CmsLayout } from "./components/CmsLayout";
import { useQuery } from "@tanstack/react-query";
import { researchCms } from "@/api/researchCms.service";
import type { PublicationRow } from "./data/mockData";
import { sortPublications } from "./utils/sortPublications";
import { publicationProgressPercent } from "./utils/publicationProgress";
import { getTrafficLightColor } from "./utils/trafficLight";
import styles from "./ResearchCmsHome.module.css";

type ActiveFilter = { key: string; label: string };

export function ResearchCmsHome() {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQuery = searchParams.get("search") ?? "";
  const editionFilter = searchParams.get("edition") ?? "";
  const statusFilter = searchParams.get("status") ?? "";
  const sortBy = searchParams.get("sort") ?? "name_asc";

  const {
    data: apiData,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ["research-publications"],
    queryFn: async () => {
      const result = await researchCms.getPublications();
      return result.publications.flatMap(pub =>
        pub.editions.map(edition => ({
          ...edition,
          publicationConfigId: pub.id,
          name: pub.name,
          directory: pub.name,
          templateType: pub.templateType,
          edition: edition.editionName ?? "-",
          status: edition.status as PublicationRow["status"],
        })),
      );
    },
  });

  const publications = apiData ?? [];

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      });
    },
    [setSearchParams],
  );

  const clearParam = useCallback((key: string) => setParam(key, ""), [setParam]);

  const clearAll = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("edition");
      next.delete("status");
      next.delete("search");
      next.delete("sort");
      return next;
    });
  }, [setSearchParams]);

  const editionOptions = useMemo(() => {
    const editions = [...new Set(publications.map(p => p.edition))].sort().reverse();
    return editions;
  }, [publications]);

  const statusOptions = useMemo(() => {
    const statuses = [...new Set(publications.map(p => p.status))];
    return statuses;
  }, [publications]);

  const sortLabels: Record<string, string> = {
    name_asc: "Name (A-Z)",
    name_desc: "Name (Z-A)",
    progress_desc: "Progress (High-Low)",
    progress_asc: "Progress (Low-High)",
  };

  const activeFilters = useMemo(() => {
    const pills: ActiveFilter[] = [];
    if (editionFilter) pills.push({ key: "edition", label: `Edition: ${editionFilter}` });
    if (statusFilter)
      pills.push({
        key: "status",
        label: `Status: ${getStatusLabel(statusFilter)}`,
      });
    if (sortBy && sortBy !== "name_asc") {
      pills.push({ key: "sort", label: `Sort: ${sortLabels[sortBy] ?? sortBy}` });
    }
    return pills;
  }, [editionFilter, statusFilter, sortBy]);

  const filtered = useMemo(() => {
    const result = publications.filter(pub => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        pub.name.toLowerCase().includes(q) ||
        pub.edition.toLowerCase().includes(q) ||
        pub.templateType.toLowerCase().includes(q);
      const matchesEdition = !editionFilter || pub.edition === editionFilter;
      const matchesStatus = !statusFilter || pub.status === statusFilter;
      return matchesSearch && matchesEdition && matchesStatus;
    });

    return sortPublications(result, sortBy);
  }, [publications, searchQuery, editionFilter, statusFilter, sortBy]);

  const grouped = useMemo(() => {
    const groups: Record<string, PublicationRow[]> = {};
    for (const pub of filtered) {
      const dir = pub.directory;
      if (!groups[dir]) groups[dir] = [];
      groups[dir].push(pub);
    }
    return Object.entries(groups);
  }, [filtered]);

  const tableGroups = useMemo(
    () =>
      grouped.map(([directory, pubs]) => ({
        name: directory,
        count: pubs.length,
        rows: pubs,
      })),
    [grouped],
  );

  const columns = useMemo(
    () => [
      {
        key: "edition",
        render: (pub: PublicationRow) => <span>{pub.edition}</span>,
      },
      {
        key: "status",
        width: "160px",
        render: (pub: PublicationRow) => <StatusBadge status={pub.status} />,
      },
      {
        key: "pct",
        width: "52px",
        render: (pub: PublicationRow) => {
          const pct = publicationProgressPercent(pub);
          return (
            <span
              style={{
                fontSize: "13px",
                fontWeight: 700,
                textAlign: "right",
                display: "block",
                fontVariantNumeric: "tabular-nums",
                color: pct !== undefined && pct > 0 ? getTrafficLightColor(pct) : undefined,
              }}
            >
              {pct === undefined ? "—" : `${pct}%`}
            </span>
          );
        },
      },
      {
        key: "bar",
        width: "220px",
        render: (pub: PublicationRow) => {
          const pct = publicationProgressPercent(pub);
          if (pct === undefined) return null;

          const { pendingCount, readyForEditingCount, readyForReleaseCount } = pub;
          if (
            pendingCount === undefined ||
            readyForEditingCount === undefined ||
            readyForReleaseCount === undefined
          ) {
            return null;
          }

          return (
            <CmsStackedBar
              complete={pub.completedBpas ?? 0}
              inProgress={pub.inProgressBpas ?? 0}
              total={pub.bpaCount ?? 0}
              variant="review-status"
              reviewStatusCounts={{
                pending: pendingCount,
                readyForEditing: readyForEditingCount,
                readyForRelease: readyForReleaseCount,
              }}
            />
          );
        },
      },
    ],
    [],
  );

  if (!hasPermission("staff.research.read")) return <UnauthorizedView />;

  if (loading) {
    return (
      <CmsLayout>
        <PageHeader title="Research Portal" subtitle="Loading publications..." />
      </CmsLayout>
    );
  }

  if (error) {
    return (
      <CmsLayout>
        <PageHeader title="Research Portal" subtitle="Failed to load publications" />
        <div style={{ padding: 24, color: "var(--color-danger)" }}>{error.message}</div>
      </CmsLayout>
    );
  }

  return (
    <CmsLayout>
      <PageHeader title="Research Portal" subtitle="Select a publication to begin" />
      <div className={styles.wrapper}>
        <SearchBar
          value={searchQuery}
          onChange={val => setParam("search", val)}
          onClear={() => clearParam("search")}
          placeholder="Search publications..."
        />

        <CmsFilterRow
          filters={[
            {
              key: "edition",
              label: "Edition",
              value: editionFilter,
              onChange: val => setParam("edition", val),
              options: editionOptions.map(e => ({ value: e, label: e })),
              placeholder: "All years",
            },
            {
              key: "status",
              label: "Status",
              value: statusFilter,
              onChange: val => setParam("status", val),
              options: statusOptions.map(s => ({
                value: s,
                label: getStatusLabel(s),
              })),
              placeholder: "All statuses",
            },
            {
              key: "sort",
              label: "Sort by",
              value: sortBy,
              onChange: val => setParam("sort", val),
              options: [
                { value: "name_asc", label: "Name (A-Z)" },
                { value: "name_desc", label: "Name (Z-A)" },
                { value: "progress_desc", label: "Progress (High-Low)" },
                { value: "progress_asc", label: "Progress (Low-High)" },
              ],
              placeholder: "",
            },
          ]}
          activeFilters={activeFilters}
          onClearFilter={clearParam}
          onClearAll={clearAll}
        />

        {filtered.length === 0 ? (
          publications.length === 0 ? (
            <CmsEmptyState message="No publications yet." />
          ) : (
            <CmsEmptyState
              message="No publications match your filters."
              action={{ label: "Clear filters", onClick: clearAll }}
            />
          )
        ) : (
          <CmsGroupedTable
            groups={tableGroups}
            columns={columns}
            onRowClick={pub =>
              navigate(ROUTES.research.cmsPublication(pub.editionId), {
                state: { templateType: pub.templateType },
              })
            }
            rowKey={pub => pub.id}
          />
        )}
      </div>
    </CmsLayout>
  );
}
